import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { complianceService } from '../../services/api';

interface Exception {
    priority: 'critical' | 'warning' | 'info';
    propertyName: string;
    issue: string;
    detail?: string;
    dueDate?: string;
    propertyId: string;
    type: string;
}

interface ExceptionFeedProps {
    groupId?: string;
    onPropertyClick?: (id: string) => void;
    searchTerm?: string;
}

const PAGE_SIZE = 10;

const ExceptionFeed: React.FC<ExceptionFeedProps> = ({ groupId, onPropertyClick, searchTerm = '' }) => {
    const navigate = useNavigate();
    const [exceptions, setExceptions] = useState<Exception[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    useEffect(() => {
        const fetchExceptions = async () => {
            if (loading) setLoading(true);
            else setRefreshing(true);

            try {
                const res = await complianceService.getExceptionFeed(groupId);
                if (res.data?.success) {
                    setExceptions(res.data.data);
                    setVisibleCount(PAGE_SIZE); // reset pagination on fresh load
                }
            } catch (err) {
                console.error('[ExceptionFeed] Failed to fetch exceptions:', err);
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        };
        fetchExceptions();
    }, [groupId]);

    const filteredExceptions = exceptions.filter(ex =>
        (ex.propertyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ex.issue || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const visibleExceptions = filteredExceptions.slice(0, visibleCount);
    const hasMore = visibleCount < filteredExceptions.length;
    const remaining = filteredExceptions.length - visibleCount;

    const getIcon = (type: string) => {
        switch (type) {
            case 'missing_certs': return 'fa-file-circle-exclamation';
            case 'expired_cert':  return 'fa-circle-xmark';
            case 'expiring_cert': return 'fa-clock';
            default:              return 'fa-triangle-exclamation';
        }
    };

    if (loading) {
        return (
            <div className="hp-feed-container space-y-4">
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className="hp-card h-24 bg-slate-100/50 animate-pulse rounded-xl"></div>
                ))}
            </div>
        );
    }

    return (
        <div className={`hp-feed-container transition-opacity duration-200 ${refreshing ? 'opacity-50' : 'opacity-100'}`}>
            {/* Header */}
            <div className="flex justify-between items-center mb-4 px-1">
                <div className="flex items-center gap-3">
                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Live Exceptions</h3>
                    <div className="bg-rose-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow-sm">
                        {filteredExceptions.length}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button className="text-[10px] font-extrabold text-slate-400 hover:text-slate-900 uppercase tracking-tighter transition-colors">
                         Priority View <i className="fas fa-sort-amount-down ml-1"></i>
                    </button>
                </div>
            </div>

            {filteredExceptions.length > 0 ? (
                <>
                    <div className="hp-exception-grid">
                        {visibleExceptions.map((ex, idx) => (
                            <div key={`${ex.propertyId}-${idx}`} className={`hp-exception-card group ${ex.priority}`}>
                                <div className="flex justify-between items-start">
                                    <div className={`hp-exception-icon ${
                                        ex.priority === 'critical' ? 'bg-rose-50 text-rose-500' : 'bg-amber-50 text-amber-600'
                                    } shadow-sm`}>
                                        <i className={`fas ${getIcon(ex.type)}`}></i>
                                    </div>
                                    {ex.dueDate && (
                                        <span className="text-[9px] font-bold text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                                            {new Date(ex.dueDate).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>

                                <div className="mt-2 flex-1">
                                    <h4 className="hp-exception-property truncate">{ex.propertyName}</h4>
                                    <p className="hp-exception-issue line-clamp-2">{ex.issue}</p>
                                    {ex.detail && (
                                        <p className="text-[10px] text-slate-400 mt-1 font-medium truncate opacity-0 group-hover:opacity-100 transition-opacity">
                                            {ex.detail}
                                        </p>
                                    )}
                                </div>

                                <button
                                    className="hp-btn-action mt-3 group-hover:bg-emerald-600 group-hover:border-emerald-600 transition-all"
                                    onClick={() => onPropertyClick ? onPropertyClick(ex.propertyId) : navigate(`/properties/${ex.propertyId}`)}
                                >
                                    Resolve Issue
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Load More button */}
                    {hasMore && (
                        <div className="mt-5 text-center">
                            <button
                                onClick={() => setVisibleCount(v => v + PAGE_SIZE)}
                                className="text-xs font-black text-slate-500 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 border border-slate-200 px-6 py-2.5 rounded-full transition-all uppercase tracking-widest"
                            >
                                Load {Math.min(remaining, PAGE_SIZE)} more
                                <span className="ml-2 bg-slate-200 text-slate-600 text-[10px] font-black px-1.5 py-0.5 rounded-full">
                                    {remaining} left
                                </span>
                            </button>
                        </div>
                    )}

                    {/* All-loaded indicator */}
                    {!hasMore && filteredExceptions.length > PAGE_SIZE && (
                        <p className="text-center text-[10px] text-slate-400 font-bold mt-4 uppercase tracking-widest">
                            All {filteredExceptions.length} exceptions shown
                        </p>
                    )}
                </>
            ) : (
                <div className="py-24 text-center bg-white rounded-3xl border border-dashed border-slate-200">
                    <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center text-2xl mx-auto mb-4 border-2 border-white shadow-sm">
                        <i className="fas fa-check-shield"></i>
                    </div>
                    <h4 className="text-sm font-black text-slate-800 uppercase tracking-tighter">Everything under control</h4>
                    <p className="text-xs text-slate-400 font-medium mt-1">Operational standards are fully satisfied.</p>
                </div>
            )}
        </div>
    );
};

export default ExceptionFeed;
